// A station class's **Windows & games** (F-BOOT-27, ADR-0065 decision 5; spec/13 §12): what the class's Clavian-booted PCs
// start — a Windows image of the venue, or the venue's default, and its game disks, or the image's own. A tab of the class's
// Edit dialog, shown where the venue has Clavian Boot and the member may view it; changed with `boot:manage` and
// `stations:manage` (`PUT …/boot/classes/{classId}`), read-only otherwise. A PC with its own choice, set in its window on
// the Floor, keeps it; the others switch at their next restart when nobody is playing.

import type { BootClass, BootImage } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, RadioGroup, SelectField, Skeleton, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { problemDetail } from '../api/problems';
import { LoadFailure } from '../shell/LoadFailure';
import { Notice } from '../ui';
import { useBootClasses, useBootImages, useUpdateBootClass, type BootScope } from './queries';

import './class-boot.css';

const DEFAULT = 'default';

/** The images a class may be given: live system images with a published version, and live game disks. */
export function classChoices(images: readonly BootImage[]): { readonly systems: readonly BootImage[]; readonly disks: readonly BootImage[] } {
  const live = images.filter((image) => image.archivedAt === null && image.currentVersionId !== null);
  return { systems: live.filter((image) => image.kind === 'system'), disks: live.filter((image) => image.kind === 'game') };
}

/** The class's image in words, for its card: its own image's name, or the venue default's. Null without Clavian Boot data. */
export function classWindowsName(bootClass: BootClass | undefined, images: readonly BootImage[] | undefined, t: ReturnType<typeof useTranslation>['t']): string | null {
  if (bootClass === undefined || images === undefined) return null;
  const own = bootClass.imageId === null ? undefined : images.find((image) => image.id === bootClass.imageId);
  if (own !== undefined) return own.name;
  const fallback = images.find((image) => image.kind === 'system' && image.defaultForVenue && image.archivedAt === null);
  return fallback === undefined ? t('boot.classes.imageDefaultNone') : t('boot.classes.imageDefault', { name: fallback.name });
}

export function ClassBoot({ scope, classId, canEdit }: { readonly scope: BootScope; readonly classId: string; readonly canEdit: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const classes = useBootClasses(scope);
  const images = useBootImages(scope);
  if (classes.data === undefined || images.data === undefined) {
    const failed = classes.isError ? classes : images.isError ? images : undefined;
    return failed === undefined ? <Skeleton shape="text" lines={4} /> : <LoadFailure error={failed.error} message={t('boot.classes.failed')} onRetry={() => void failed.refetch()} />;
  }
  const current = classes.data.find((entry) => entry.classId === classId) ?? { classId, imageId: null, gameDiskIds: null };
  return <ClassBootForm key={`${current.imageId ?? ''}:${(current.gameDiskIds ?? []).join(',')}`} scope={scope} current={current} images={images.data} canEdit={canEdit} />;
}

function ClassBootForm({ scope, current, images, canEdit }: { readonly scope: BootScope; readonly current: BootClass; readonly images: readonly BootImage[]; readonly canEdit: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const update = useUpdateBootClass(scope);
  const { systems, disks } = classChoices(images);
  const venueDefault = systems.find((image) => image.defaultForVenue);
  const [imageChoice, setImageChoice] = useState<string>(current.imageId ?? DEFAULT);
  const [gamesMode, setGamesMode] = useState<'image' | 'own'>(current.gameDiskIds === null ? 'image' : 'own');
  const [picked, setPicked] = useState<readonly string[]>(current.gameDiskIds ?? []);
  const [gamesError, setGamesError] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const changed =
    imageChoice !== (current.imageId ?? DEFAULT) ||
    (gamesMode === 'image') !== (current.gameDiskIds === null) ||
    (gamesMode === 'own' && [...picked].sort().join(',') !== [...(current.gameDiskIds ?? [])].sort().join(','));

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFailure(undefined);
    if (gamesMode === 'own' && picked.length === 0) {
      setGamesError(t('boot.classes.gamesRequired'));
      return;
    }
    setGamesError(undefined);
    update.mutate(
      { classId: current.classId, body: { imageId: imageChoice === DEFAULT ? null : imageChoice, gameDiskIds: gamesMode === 'image' ? null : [...picked] } },
      {
        onSuccess: () => show({ tone: 'ok', title: t('boot.classes.saved') }),
        onError: (caught) => setFailure(problemDetail(caught) ?? t('boot.classes.failed')),
      },
    );
  }

  return (
    <form className="console-form console-class-boot" noValidate aria-label={t('boot.classes.tab')} onSubmit={submit}>
      <p className="console-subtle">{t('boot.classes.intro')}</p>
      {canEdit ? null : <Notice role="note">{t('boot.classes.readOnly')}</Notice>}
      {failure === undefined ? null : (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      )}
      {systems.length === 0 ? <Notice tone="warn">{t('boot.classes.noImages')}</Notice> : null}
      <SelectField
        label={t('boot.classes.image')}
        value={imageChoice}
        onValueChange={setImageChoice}
        disabled={!canEdit}
        options={[
          { value: DEFAULT, label: venueDefault === undefined ? t('boot.classes.imageDefaultNone') : t('boot.classes.imageDefault', { name: venueDefault.name }) },
          ...systems.map((image) => ({ value: image.id, label: image.name })),
        ]}
      />
      {disks.length === 0 ? null : (
        <>
          <RadioGroup
            label={t('boot.classes.games')}
            value={gamesMode}
            disabled={!canEdit}
            onValueChange={(value) => setGamesMode(value === 'own' ? 'own' : 'image')}
            options={[
              { value: 'image', label: t('boot.classes.gamesDefault') },
              { value: 'own', label: t('boot.classes.gamesChoose') },
            ]}
          />
          {gamesMode === 'own' ? (
            <fieldset className="console-class-boot__disks" disabled={!canEdit}>
              <legend className="console-subtle">{t('boot.classes.gamesPick')}</legend>
              {disks.map((disk) => (
                <Checkbox
                  key={disk.id}
                  label={disk.driveLetter === null ? disk.name : t('boot.floor.facts.gameDisk', { name: disk.name, letter: disk.driveLetter })}
                  checked={picked.includes(disk.id)}
                  onCheckedChange={(checked) => setPicked((now) => (checked === true ? [...now.filter((id) => id !== disk.id), disk.id] : now.filter((id) => id !== disk.id)))}
                />
              ))}
              {gamesError === undefined ? null : (
                <p className="console-field-error" role="alert">
                  {gamesError}
                </p>
              )}
            </fieldset>
          ) : null}
        </>
      )}
      {canEdit ? (
        <div className="console-form__actions">
          <Button type="submit" variant="primary" busy={update.isPending} disabled={!changed}>
            {t('boot.classes.save')}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
